import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ELEMENT_CATEGORIES,
  TAKEAWAYS_NEED_BREAKDOWN_CODE,
  TAKEAWAYS_OFF_CODE,
  type SceneTakeawayView,
  type SceneTakeawaysView,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { useCan, useRefusal } from '../../lib/permissions.js';
import { useScriptRun, useTakeawaysRun } from '../../lib/useScriptRun.js';
import { Callout } from '../Callout.js';
import { AiCostApproval } from '../credits/AiCostApproval.js';
import { aiBillingMode, refusalCode, useProjectCredits } from '../credits/useProjectCredits.js';
import { Icon } from '../Icon.js';
import { StatusPill } from '../ui/StatusPill.js';
import { useSceneBreakdown } from './context.js';
import {
  FRESHNESS_REASON_TEXT,
  TAKEAWAY_FLAG_TEXT,
  TAKEAWAY_KIND_LABEL,
  TAKEAWAY_STATUS_TEXT,
  evidenceLabel,
  sceneName,
} from './labels.js';

/**
 * KEY TAKEAWAYS — an AI-assisted reading of what matters most in the scene, written FROM its
 * breakdown and never in place of it (owner decisions D2, D5; rules 6 and 7).
 *
 * Every takeaway points at the items it rests on (chips that open the item) or at a verified
 * quote (Show in script), or it was dropped by the server. The heading says what it is — an
 * AI-assisted reading, not a production plan — so nothing here reads as a fact or a decision.
 *
 * FRESHNESS IS THE SERVER'S (`takeawayFreshness`). An out-of-date reading says why, in the
 * server's reasons; one takeaway resting on an item marked not needed, or naming a decision made
 * since, says so on its own line. Accepting an item does not age a reading, by rule.
 *
 * Generating them is a paid reading of its own (`scope: TAKEAWAYS`), started the way the
 * breakdown's is (`TakeawaysRun`). The button is a plain control, never the page's red one:
 * reviewing the scene is the work, the reading is help with it.
 */
export function KeyTakeawaysCard() {
  const { projectId, scriptVersionId, detail, row, onReadingStarted } = useSceneBreakdown();
  const [approval, setApproval] = useState<number | null>(null);
  const canRun = useCan('AI_RUN');
  const runRefusal = useRefusal('AI_RUN');
  // A reading of this draft in flight (a breakdown or takeaways) refuses another: say so on the button.
  // Any reading of this draft in flight, a breakdown or takeaways, refuses another (one at a time).
  const breakdownRun = useScriptRun(scriptVersionId);
  const takeawaysRun = useTakeawaysRun(scriptVersionId);
  const inFlight = breakdownRun.inFlight || takeawaysRun.inFlight;

  if (detail === null) return null;

  const reading = detail.takeaways;
  const sceneId = detail.scene.id;
  const hasBreakdown = detail.counts.items > 0 || detail.items.length > 0;

  return (
    <section className="el-takeaways" aria-labelledby={`el-takeaways-${sceneId}`} data-testid="el-takeaways">
      <header className="el-takeaways-head">
        <span className="el-takeaways-mark" aria-hidden="true">
          <Icon name="sparkle" size={16} />
        </span>
        <div>
          <p className="el-takeaways-title" id={`el-takeaways-${sceneId}`}>
            Key takeaways
          </p>
          <p className="el-takeaways-sub" data-testid="el-takeaways-disclaimer">
            AI-assisted reading — not a production plan
          </p>
        </div>
        {reading !== null && reading.outOfDate ? (
          <StatusPill state="OUTDATED" size="sm" testId="el-takeaways-outdated" />
        ) : null}
      </header>

      <TakeawaysBody reading={reading} indexState={row?.takeaways.state ?? null} />

      {approval === null ? (
        <div className="el-takeaways-actions">
          <button
            type="button"
            className="ctl"
            data-testid="el-takeaways-generate"
            disabled={!canRun || !hasBreakdown || inFlight}
            title={
              !canRun
                ? (runRefusal ?? undefined)
                : !hasBreakdown
                  ? 'The scene has no breakdown items to read from yet'
                  : inFlight
                    ? 'A reading of this draft is running; try again when it finishes'
                    : undefined
            }
            onClick={() => {
              setApproval((n) => (n ?? 0) + 1);
            }}
          >
            <Icon name="sparkle" size={14} />
            {inFlight ? 'Reading…' : reading === null ? 'Generate key takeaways' : 'Update key takeaways'}
          </button>
          {reading !== null && reading.earlierReadings > 0 ? (
            <span className="el-takeaways-earlier" data-testid="el-takeaways-earlier">
              {`${String(reading.earlierReadings)} earlier ${reading.earlierReadings === 1 ? 'reading' : 'readings'} kept`}
            </span>
          ) : null}
        </div>
      ) : (
        <TakeawaysRun
          key={approval}
          projectId={projectId}
          scriptVersionId={scriptVersionId}
          sceneIds={[sceneId]}
          sceneLabel={() => sceneName(detail.scene)}
          testId="el-takeaways-approval"
          onClose={() => {
            setApproval(null);
          }}
          onStarted={({ legacy }) => {
            // The unmetered start has nothing more to show; the metered approval says it started.
            if (legacy) setApproval(null);
            onReadingStarted();
          }}
        />
      )}
    </section>
  );
}

/** Scene names for a refusal, a long list cut short rather than run on. */
function namesOf(sceneIds: readonly string[], sceneLabel: (sceneId: string) => string): string {
  const shown = sceneIds.slice(0, 6).map(sceneLabel);
  const more = sceneIds.length - shown.length;

  return more > 0 ? `${shown.join(', ')} and ${String(more)} more` : shown.join(', ');
}

/** A refusal of the takeaways request, in the workspace's words. */
function TakeawaysRefusal({
  error,
  sceneIds,
  sceneLabel,
  testId,
}: {
  error: Error;
  sceneIds: readonly string[];
  sceneLabel: (sceneId: string) => string;
  testId: string;
}) {
  const code = error instanceof ApiError ? error.code : null;

  if (code === TAKEAWAYS_OFF_CODE) {
    return (
      <Callout tone="warn" title="Key takeaways are switched off" testId={testId}>
        They are not available in this workspace yet. Nothing was started or charged.
      </Callout>
    );
  }

  if (code === TAKEAWAYS_NEED_BREAKDOWN_CODE) {
    // The server names the scenes no finished breakdown has read; the selection is the fallback.
    const named = (error instanceof ApiError ? error.detail : null) as { sceneIds?: unknown } | null | undefined;
    const missing = Array.isArray(named?.sceneIds)
      ? named.sceneIds.filter((id): id is string => typeof id === 'string')
      : [...sceneIds];
    const one = missing.length === 1;

    return (
      <Callout tone="warn" title="Read the breakdown first" testId={testId}>
        {one
          ? `${namesOf(missing, sceneLabel)} has no AI breakdown yet, so there is nothing to read takeaways from. Read it with AI first.`
          : `${namesOf(missing, sceneLabel)} have no AI breakdown yet, so there is nothing to read takeaways from. Read them with AI first.`}
      </Callout>
    );
  }

  if (code === 'EXTRACTION_ALREADY_RUNNING') {
    return (
      <Callout tone="info" title="A reading of this draft is already running" testId={testId}>
        Key takeaways can be read once it finishes. Nothing was started or charged.
      </Callout>
    );
  }

  return (
    <Callout tone="danger" title="Could not start the reading" testId={testId}>
      {error.message}
    </Callout>
  );
}

/**
 * STARTING A KEY TAKEAWAYS READING, DECIDED THE WAY THE BREAKDOWN'S IS (`AnalyseWithAi`).
 *
 * Where the platform stands comes from the wallet (`aiBillingMode`, unknown reads LEGACY):
 *   - LEGACY — the legacy verb, `POST …/extract` with `{ sceneIds, scope: 'TAKEAWAYS' }`, after the
 *     paid-request note and one confirming click. Every environment that has not launched metered AI
 *     is here, and there the estimate is refused, so the approval alone would never start anything.
 *   - METERED — the estimate and approval (`AiCostApproval`, `scope: TAKEAWAYS`); a member without
 *     `AI_RUN` is told so instead.
 *   - SUSPENDED — nothing starts ("AI analysis is paused").
 * The server is the gate either way: a metered production refuses the legacy verb with
 * `BILLING_APPROVAL_REQUIRED`, which opens the approval, and `AI_BILLING_UNAVAILABLE` pauses.
 * The takeaways refusals (`TAKEAWAYS_OFF`, `TAKEAWAYS_NEED_BREAKDOWN` naming its scenes,
 * `EXTRACTION_ALREADY_RUNNING`) are said in words; nothing was charged for any of them.
 */
export function TakeawaysRun({
  projectId,
  scriptVersionId,
  sceneIds,
  sceneLabel,
  testId,
  primary = false,
  onClose,
  onStarted,
}: {
  projectId: string;
  scriptVersionId: string;
  sceneIds: readonly string[];
  /** "Scene 12" for a scene id, to name the scenes a refusal names. */
  sceneLabel: (sceneId: string) => string;
  /** The approval's test id; the unmetered controls derive theirs from it. */
  testId: string;
  /** In a dialog the start is that dialog's one primary; inside a panel it is a plain control. */
  primary?: boolean;
  onClose: () => void;
  onStarted: (how: { legacy: boolean }) => void;
}) {
  const credits = useProjectCredits(projectId);
  const canRun = useCan('AI_RUN');
  const runRefusal = useRefusal('AI_RUN');
  // Any reading of this draft in flight, a breakdown or takeaways, refuses another (one at a time).
  const breakdownRun = useScriptRun(scriptVersionId);
  const takeawaysRun = useTakeawaysRun(scriptVersionId);
  const inFlight = breakdownRun.inFlight || takeawaysRun.inFlight;
  // What the server said about the legacy verb, kept: it outlives the refusal.
  const [serverSaid, setServerSaid] = useState<'METERED' | 'SUSPENDED' | null>(null);

  const start = useMutation({
    mutationFn: () => api.requestExtraction(projectId, scriptVersionId, sceneIds, 'TAKEAWAYS'),
    onSuccess: () => {
      onStarted({ legacy: true });
    },
    onError: (error: Error) => {
      // Metered after all (the wallet had not said so yet): ask, as metered does.
      if (refusalCode(error) === 'BILLING_APPROVAL_REQUIRED') {
        setServerSaid('METERED');
        void credits.refetch();
      }
      if (refusalCode(error) === 'AI_BILLING_UNAVAILABLE') {
        setServerSaid('SUSPENDED');
        void credits.refetch();
      }
    },
  });

  const mode = aiBillingMode(credits.data);
  const paused = mode === 'SUSPENDED' || (mode !== 'METERED' && serverSaid === 'SUSPENDED');
  const metered = !paused && (mode === 'METERED' || serverSaid === 'METERED');
  const count = sceneIds.length;

  if (paused) {
    return (
      <div className="el-takeaways-run" data-testid={`${testId}-paused`}>
        <Callout tone="warn" title="AI analysis is paused">
          New AI work cannot be started right now. Anything already running finishes and is charged as usual, and the
          credits on this production are safe.
        </Callout>
        <button type="button" className="ctl" data-testid={`${testId}-cancel`} onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  if (metered && !canRun) {
    return (
      <div className="el-takeaways-run" data-testid={`${testId}-not-allowed`}>
        <p className="el-takeaways-empty">{runRefusal ?? 'Your role on this production does not run AI.'}</p>
        <button type="button" className="ctl" data-testid={`${testId}-cancel`} onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  if (metered) {
    return (
      <AiCostApproval
        projectId={projectId}
        operationType="SCRIPT_ANALYSIS"
        scriptVersionId={scriptVersionId}
        sceneIds={sceneIds}
        scope="TAKEAWAYS"
        testId={testId}
        onClose={onClose}
        onStarted={() => {
          onStarted({ legacy: false });
        }}
      />
    );
  }

  const legacyRefusal = refusalCode(start.error);
  const showRefusal =
    start.error !== null && legacyRefusal !== 'BILLING_APPROVAL_REQUIRED' && legacyRefusal !== 'AI_BILLING_UNAVAILABLE';

  return (
    <div className="el-takeaways-run" data-testid={`${testId}-legacy`}>
      <p className="el-takeaways-empty" data-testid={`${testId}-note`}>
        {`Key takeaways are a paid AI reading of ${count === 1 ? 'this scene’s breakdown' : `the breakdown of these ${String(count)} scenes`} as it is now. They are never read for you: nothing runs until you start it.`}
      </p>
      {showRefusal && start.error !== null ? (
        <TakeawaysRefusal error={start.error} sceneIds={sceneIds} sceneLabel={sceneLabel} testId={`${testId}-refusal`} />
      ) : null}
      <div className="el-takeaways-actions">
        <button
          type="button"
          className={primary ? 'btn btn-primary' : 'ctl'}
          data-testid={`${testId}-start`}
          // As the breakdown's own button before metering: offered to every role, the server explains a refusal.
          disabled={start.isPending || inFlight}
          title={inFlight ? 'A reading of this draft is running' : undefined}
          onClick={() => {
            start.mutate();
          }}
        >
          {start.isPending ? 'Starting…' : count === 1 ? 'Read key takeaways' : `Read key takeaways for ${String(count)} scenes`}
        </button>
        <button type="button" className="ctl" data-testid={`${testId}-cancel`} disabled={start.isPending} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function TakeawaysBody({
  reading,
  indexState,
}: {
  reading: SceneTakeawaysView | null;
  indexState: string | null;
}) {
  if (reading === null) {
    return (
      <p className="el-takeaways-empty" data-testid="el-takeaways-none" data-index-state={indexState ?? ''}>
        Key takeaways come from the breakdown; generate them for this scene.
      </p>
    );
  }

  return (
    <>
      {reading.outOfDate ? (
        <div className="el-takeaways-stale" data-testid="el-takeaways-stale" role="note">
          <p>Out of date since this reading:</p>
          <ul>
            {reading.reasons.map((reason) => (
              <li key={reason} data-testid={`el-takeaways-reason-${reason}`}>
                {FRESHNESS_REASON_TEXT[reason]}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {reading.status === 'READ' ? (
        <ol className="el-takeaway-list" data-testid="el-takeaways-list">
          {reading.takeaways.map((takeaway, index) => (
            <Takeaway key={`${takeaway.kind}-${String(index)}`} takeaway={takeaway} index={index} />
          ))}
        </ol>
      ) : (
        <p className="el-takeaways-empty" data-testid={`el-takeaways-status-${reading.status}`}>
          {TAKEAWAY_STATUS_TEXT[reading.status]}
        </p>
      )}
    </>
  );
}

function Takeaway({ takeaway, index }: { takeaway: SceneTakeawayView; index: number }) {
  const { selectItem, showRanges } = useSceneBreakdown();

  return (
    <li className="el-takeaway" data-kind={takeaway.kind} data-testid={`el-takeaway-${String(index)}`}>
      <p className="el-takeaway-kind" data-testid={`el-takeaway-kind-${String(index)}`}>
        {TAKEAWAY_KIND_LABEL[takeaway.kind]}
      </p>
      <p className="el-takeaway-text">{takeaway.text}</p>

      {takeaway.flag === null ? null : (
        <p className="el-takeaway-flag" data-testid={`el-takeaway-flag-${String(index)}`}>
          <Icon name="alert" size={13} />
          {TAKEAWAY_FLAG_TEXT[takeaway.flag]}
        </p>
      )}

      {takeaway.refs.length === 0 && takeaway.evidence === null ? null : (
        <div className="el-takeaway-refs">
          {takeaway.refs.map((ref) =>
            ref.kind === 'ITEM' ? (
              <button
                key={`${ref.kind}-${ref.key}`}
                type="button"
                className="el-ref"
                disabled={!ref.present}
                title={ref.present ? 'Open this item' : 'No longer in this scene'}
                data-testid={`el-takeaway-ref-${String(index)}-${ref.key}`}
                onClick={() => {
                  selectItem(ref.key);
                }}
              >
                {ref.category === null ? null : (
                  <i aria-hidden="true" className="el-swatch" style={{ background: ELEMENT_CATEGORIES[ref.category].colour }} />
                )}
                {ref.label}
                {ref.present ? null : <span className="sr-only"> (no longer in this scene)</span>}
              </button>
            ) : (
              <span key={`${ref.kind}-${ref.key}`} className="el-ref el-ref-static" data-testid={`el-takeaway-ref-${String(index)}-${ref.key}`}>
                {ref.label}
              </span>
            ),
          )}
          {takeaway.evidence === null || takeaway.evidence.ranges.length === 0 ? null : (
            <button
              type="button"
              className="el-link"
              data-testid={`el-takeaway-show-${String(index)}`}
              title={evidenceLabel(takeaway.evidence)}
              onClick={() => {
                if (takeaway.evidence !== null) showRanges(takeaway.evidence);
              }}
            >
              Show in script
            </button>
          )}
        </div>
      )}
    </li>
  );
}
